import type { MessagesOf, PlainKeyOf } from "../translate";

/**
 * Bahasa Indonesia, the default language and the source of the key set (R1-09e): every key here needs
 * an English entry in `en.ts`, or `bun run typecheck` fails. Keys are `area.thing`; `{name}` marks a
 * placeholder. Indonesian nouns don't change with a count, so this file has no `.one` forms.
 *
 * This core dictionary holds what the shell, the register and the kit show, and loads with the app.
 * Each admin area (catalogue, staff, sign-in…) has its own module next to this one (`defineArea`),
 * shipped with its routes (R1-09f).
 *
 * Glossary, so POS terms read the same on every screen:
 *
 * | Indonesian      | English            |
 * | --------------- | ------------------ |
 * | Kasir           | Register / Cashier |
 * | Bayar           | Pay                |
 * | Pesanan         | Order              |
 * | Struk           | Receipt            |
 * | Shift           | Shift              |
 * | Modal awal      | Opening float      |
 * | Kas masuk       | Paid in            |
 * | Kas keluar      | Paid out           |
 * | Setor kas       | Cash drop          |
 * | Hitung buta     | Blind count        |
 * | Selisih         | Variance           |
 * | Bahan           | Ingredient         |
 * | Resep           | Recipe             |
 * | Varian          | Variant            |
 * | Modifier        | Modifier           |
 * | Makan di tempat | Dine-in            |
 * | Bawa pulang     | Takeaway           |
 * | Antar           | Delivery           |
 *
 * "Kasir" is the Register when it means the screen or the device, and the Cashier when it means the
 * person.
 */
export const id = {
  "common.cancel": "Batal",
  "common.retry": "Coba lagi",
  "common.online": "Online",
  "common.offline": "Offline",
  "common.activate": "Aktifkan",
  "common.deactivate": "Nonaktifkan",
  "common.save": "Simpan",
  "common.saving": "Menyimpan…",
  "common.active": "Aktif",
  "common.inactive": "Nonaktif",
  "common.allStatuses": "Semua status",
  "common.status": "Status",
  "common.name": "Nama",
  "common.saveOffline": "Tidak bisa menyimpan. Periksa koneksi lalu coba lagi.",
  "common.saveFailed": "Tidak bisa menyimpan. Coba lagi sebentar lagi.",

  "startup.failed": "Aplikasi belum bisa dibuka",
  "startup.failedHint":
    "Penyimpanan atau navigasi perangkat belum siap. Coba lagi. Jika tetap gagal, hubungi pengelola; jangan hapus data aplikasi.",

  "kit.close": "Tutup",
  "kit.searchPlaceholder": "Cari…",
  "kit.search": "Cari",
  "kit.noMatch": "Tidak ada yang cocok.",
  "kit.all": "Semua",
  "kit.optionsFailed": "Tidak bisa memuat pilihan.",
  "kit.loadFailed": "Gagal memuat data",
  "kit.loadFailedHint": "Periksa koneksi, lalu coba lagi.",
  "kit.noResults": "Tidak ada hasil",
  "kit.noResultsFor": "Tidak ada data yang cocok dengan “{query}”.",
  "kit.empty": "Belum ada data",
  "kit.actions": "Aksi",
  "kit.loading": "Memuat…",
  "kit.loadMore": "Muat lebih banyak",
  "kit.nextPageFailed": "Gagal memuat halaman berikutnya",
  "kit.pagination": "Halaman",
  "kit.previousPage": "Sebelumnya",
  "kit.nextPage": "Berikutnya",
  "kit.pageNumber": "Halaman {page}",
  "kit.pickDate": "Pilih tanggal",
  "kit.clearDate": "Hapus tanggal",
  "kit.sortBy": "Urutkan menurut",
  "kit.sortAscending": "Urutan naik",
  "kit.sortDescending": "Urutan turun",
  "kit.breadcrumb": "Lokasi halaman",
  "kit.more": "Lainnya",

  "nav.section.workspace": "Workspace",
  "nav.section.preferences": "Preferensi",
  "nav.group.main": "Utama",
  "nav.group.sell": "Berjualan",
  "nav.group.finance": "Keuangan & verifikasi",
  "nav.group.catalogue": "Katalog & stok",
  "nav.group.team": "Tim & perangkat",
  "nav.dashboard": "Dashboard",
  "nav.reports": "Laporan penjualan",
  "nav.cashier": "Kasir",
  "nav.catalogue": "Katalog",
  "nav.discounts": "Diskon",
  "nav.discountApprovals": "Persetujuan diskon",
  "nav.paymentExceptions": "Rekonsiliasi QRIS",
  "nav.offlineExceptions": "Rekonsiliasi offline",
  "nav.paymentVerifications": "Verifikasi QRIS statis",
  "nav.refunds": "Refund",
  "nav.kitchen": "Dapur & bar",
  "nav.staff": "Staf",
  "nav.customers": "Pelanggan",
  "nav.stock": "Stok",
  "nav.customer": "Pelanggan",
  "nav.duplicates": "Tinjau duplikat",
  "nav.devices": "Perangkat",
  "nav.audit": "Audit",
  "nav.shift": "Shift saya",
  "nav.shifts": "Shift kas",
  "nav.settings": "Pengaturan",

  "page.brand": "Brand",
  "page.tax": "Pajak & biaya layanan",
  "page.payments": "Pembayaran",
  "page.security": "Keamanan akun",
  "page.modifierGroup": "Grup modifier",
  "page.outlet": "Outlet",
  "page.product": "Produk",

  "shell.skipToContent": "Lewati ke konten",
  "shell.menu": "Menu",
  "shell.menuDescription": "Navigasi aplikasi",
  "shell.mainNav": "Navigasi utama",
  "shell.openMenu": "Buka menu",
  "shell.soon": "Segera",
  "shell.soonLabel": "{label} (segera)",
  "shell.deviceOnline": "Perangkat online",
  "shell.openShift": "Buka shift",
  "shell.shiftOpen": "Shift #{number}",
  "shell.shiftClosing": "Menunggu persetujuan",

  "outlet.heading": "Outlet",
  "outlet.none": "Belum ada outlet",
  "outlet.notChosen": "belum dipilih",
  "outlet.count": "{count} outlet",
  "outlet.single": "Satu outlet",
  "outlet.inactive": "nonaktif",
  "outlet.inactiveTag": "(nonaktif)",
  "outlet.current": "Outlet: {name}",
  "outlet.switch": "Outlet: {name}. Ganti outlet",

  "user.account": "Akun {name}",
  "user.staff": "Staf",
  "user.cashierAt": "Kasir · {register}",
  "user.security": "Keamanan akun",
  "user.switchCashier": "Ganti kasir",
  "user.signOut": "Keluar",

  "role.owner": "Pemilik",
  "role.outlet_manager": "Manajer outlet",
  "role.cashier": "Kasir",
  "role.barista": "Barista / dapur",
  "role.inventory_staff": "Staf gudang",
  "role.finance": "Keuangan",

  "channel.dine_in": "Makan di tempat",
  "channel.takeaway": "Bawa pulang",
  "channel.delivery": "Antar",

  "theme.heading": "Tema",
  "theme.current": "Tema: {choice}",
  "theme.light": "Terang",
  "theme.dark": "Gelap",
  "theme.system": "Sistem",

  "language.heading": "Bahasa",
  "language.current": "Bahasa: {name}",

  "register.title": "Mesin kasir ini",
  "register.details": "{outlet}, {register}, {connection}. Lihat detail mesin kasir",
  "register.outlet": "Outlet",
  "register.register": "Mesin",
  "register.cashier": "Kasir",
  "register.shift": "Shift",
  "register.shiftClosed": "Belum dibuka",
  "register.shiftOpen": "#{number} dibuka",
  "register.shiftClosing": "#{number} menunggu persetujuan",
  "register.connection": "Koneksi",
  "register.offline": "Jual offline",
  "register.offlineReady": "Siap sampai {time}",
  "register.offlineSnapshotPending": "Snapshot belum tersimpan",
  "register.offlineExpired": "Lease kedaluwarsa",
  "register.offlineUnavailable": "Belum disiapkan",
  "update.ready": "Versi aplikasi baru sudah siap.",
  "update.stale": "Tab ini sudah usang: versi baru dibuka di tab lain. Muat ulang untuk lanjut berjualan.",
  "update.apply": "Perbarui sekarang",
  "update.reload": "Muat ulang",
  "update.blocked": "Belum bisa: masih ada penjualan yang sedang disimpan. Coba lagi sebentar.",
  "update.failed": "Pembaruan dihentikan: data kasir ini belum bisa disimpan. Tidak ada yang diubah.",
  "register.sync": "Sinkron",
  "register.storage": "Penyimpanan perangkat",
  "register.storageUsed": "{percent}% terpakai",
  "register.storageUnknown": "Tidak diketahui",
  "register.storageNotPersistent": "bisa dihapus browser",
  "register.storageLow":
    "Penyimpanan perangkat hampir penuh. Sinkronkan sekarang dan kosongkan ruang: penjualan yang tidak bisa disimpan tidak akan selesai.",
  "register.syncTitle": "Sinkronisasi penjualan offline",
  "register.syncUpToDate": "Sudah terbaru",
  "register.syncQueued": "{count} antre",
  "register.syncExceptionsShort": "{count} perlu ditinjau",
  "register.syncRunning": "Menyinkronkan…",
  "register.syncQueue": "Penjualan antre",
  "register.syncOldest": "Antrean tertua",
  "register.syncLast": "Sinkron terakhir berhasil",
  "register.syncExceptions": "Pengecualian belum selesai",
  "register.syncNever": "Belum pernah",
  "register.syncNone": "Tidak ada",
  "register.syncNow": "Sinkronkan sekarang",
  "register.syncFull": "Sinkron ulang penuh",
  "register.syncFailed":
    "Sinkronisasi gagal. Penjualan tetap tersimpan di kasir ini dan dicoba lagi saat online.",
  "register.syncOfflineHint": "Penjualan disimpan di kasir ini sampai koneksi kembali.",

  "cashier.title": "Kasir",
  "cashier.subtitle": "Pilih menu, atur pilihan, lalu bayar.",
  "cashier.loading": "Memuat menu outlet…",
  "cashier.noOutletHint": "Tambahkan outlet dulu di Pengaturan.",
  "cashier.notAllowed": "Tidak bisa berjualan di sini",
  "cashier.notAllowedHint":
    "Peran Anda tidak mencakup penjualan di {outlet}. Pilih outlet lain atau hubungi pemilik usaha.",
  "cashier.outletClosed": "Outlet tidak aktif",
  "cashier.outletClosedHint": "{outlet} sedang nonaktif, jadi tidak ada menu untuk dijual.",
  "cashier.menuFailed": "Menu tidak bisa dimuat",
  "cashier.checkConnection": "Periksa koneksi, lalu coba lagi.",
  "cashier.search": "Cari menu",
  "cashier.searchPlaceholder": "Cari nama menu, SKU atau barcode",
  "cashier.categories": "Kategori",
  "cashier.allItems": "Semua menu",
  "cashier.available": "{count} menu tersedia",
  "cashier.found": "{count} menu untuk “{query}”",
  "cashier.emptyMenu": "Belum ada menu yang dijual di outlet ini. Atur harga dan ketersediaan di Katalog.",
  "cashier.noMatch": "Tidak ada menu yang cocok. Coba kata lain atau pilih Semua menu.",
  "cashier.menu": "Menu",
  "cashier.order": "Pesanan",
  "cashier.newOrder": "Pesanan baru",
  "cashier.items": "{count} item",
  "cashier.viewOrder": "Lihat pesanan: {items}, {total}",
  "cashier.pay": "Bayar",
  "cashier.hold": "Tahan",
  "cashier.heldOrders": "Pesanan tertahan",
  "cashier.awaitingQris": "Menunggu QRIS",
  "cashier.tableOrder": "Pesanan meja {table}",
  "cashier.added": "{name} ditambahkan",
  "cashier.unknownCode": "Kode {code} tidak ada di menu outlet ini.",
  "cashier.cartFull": "Pesanan penuh (maks. {max} baris). Bayar dulu, lalu buat pesanan baru.",
  "cashier.priceFrom": "mulai {price}",

  "cart.orderType": "Jenis pesanan",
  "cart.table": "Nomor meja",
  "cart.tablePlaceholder": "mis. 12",
  "cart.lines": "Isi pesanan",
  "cart.emptyTitle": "Mulai dengan satu cangkir",
  "cart.emptyHint": "Pilih menu untuk membuat pesanan pertama.",
  "cart.unavailable": "Tidak tersedia",
  "cart.goneItem": "Menu tidak tersedia",
  "cart.unavailableReason": "Ada menu yang tidak tersedia. Hapus dulu.",
  "cart.incomplete": "Lengkapi pilihan",
  "cart.incompleteReason": "Lengkapi pilihan wajib pada menu bertanda.",
  "cart.quantityOf": "Jumlah {name}",
  "cart.remove": "Hapus {name}",
  "cart.decrease": "Kurangi {name}",
  "cart.increase": "Tambah {name}",
  "cart.subtotal": "Subtotal ({items})",
  "cart.total": "Total",

  "line.description": "Pilih varian dan modifier, lalu tambahkan ke pesanan.",
  "line.quantity": "Jumlah",
  "line.decrease": "Kurangi jumlah",
  "line.increase": "Tambah jumlah",
  "line.add": "Tambah",
  "line.save": "Simpan",
  "line.variant": "Varian",
  "line.required": "Wajib pilih {count}",
  "line.requiredRange": "Wajib pilih {min}–{max}",
  "line.optional": "Opsional",
  "line.optionalMax": "Opsional, maks. {max}",
  "line.note": "Catatan (opsional)",
  "line.notePlaceholder": "mis. less sugar, es sedikit",
  "line.noteHint": "Catatan untuk barista; tidak mengubah harga atau resep.",

  "checkout.title": "Pembayaran",
  "checkout.back": "Kembali ke pesanan",
  "checkout.summary": "Ringkasan pesanan",
  "checkout.total": "Total tagihan",
  "checkout.cash": "Tunai",
  "checkout.tendered": "Uang diterima",
  "checkout.exactCash": "Uang pas",
  "checkout.change": "Kembalian",
  "checkout.short": "Kurang {amount}",
  "checkout.numpad": "Masukkan jumlah uang",
  "checkout.backspace": "Hapus angka terakhir",
  "checkout.doubleZero": "Tambah dua nol",
  "checkout.takePayment": "Terima uang",
  "checkout.takingPayment": "Mencatat penjualan…",
  "checkout.doneTitle": "Penjualan selesai",
  "checkout.receiptNumber": "No. struk {number}",
  "checkout.paid": "Dibayar tunai",
  "checkout.done": "Selesai",
  "checkout.errorNoShift": "Shift belum dibuka",
  "checkout.errorNoShiftHint": "Buka shift dulu di “Shift saya”, lalu coba lagi.",
  "checkout.errorLineChanged": "Ada menu yang berubah sejak pesanan dibuat. Periksa pesanan, lalu coba lagi.",
  "checkout.errorFailed": "Penjualan tidak bisa diselesaikan. Coba lagi.",
} as const;

export type MessageKey = keyof typeof id;

/** Keys whose message has no placeholders: what a label map or a route's crumb may hold. */
export type PlainMessageKey = PlainKeyOf<typeof id>;

/** The core dictionary in any language (see `MessagesOf`). */
export type Messages = MessagesOf<typeof id>;
